import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { observer } from "mobx-react";
import { RootStoreContext } from "../../mobX/RootStore";
// nodejs library that concatenates classes
import classNames from "classnames";
// @material-ui/icons
import LocationOn from "@material-ui/icons/LocationOn";
import EuroSymbolIcon from "@material-ui/icons/EuroSymbol";
import GroupIcon from "@material-ui/icons/Group";
import TodayIcon from "@material-ui/icons/Today";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PhoneIcon from "@material-ui/icons/Phone";
// @material-ui/core customComponents
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
// core components
import Footer from "components/Footer/Footer";
import Parallax from "components/Parallax/Parallax";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
// custom components
import SportappHeader from "customComponents/SportappHeader/Header";
import ClubLogoImg from "customComponents/ClubLogoImg/ClubLogoImg";

import backgroundImage from "customAssets/img/ssb_background.png";
import style from "./styles";
import {
	costPerBillingIntervalMessage,
	displayTargetGroup,
	getCategoryImageURI,
	getSafeFullName,
	mapAgeGroupEnums,
	mapDayEnumsToFullName,
} from "../../utils/Utils";
import Button from "../../components/CustomButtons/Button";

// eslint-disable-next-line react/prop-types
const MailTo = ({ email, subject, body, children }) => {
	return (
		<a
			href={`mailto:${email}?subject=${
				encodeURIComponent(subject) || ""
			}&body=${encodeURIComponent(body) || ""}`}
		>
			{children}
		</a>
	);
};

// eslint-disable-next-line react/prop-types
const PhoneCall = ({ number, children }) => {
	return <a href={`tel:${number}`}>{children}</a>;
};

// eslint-disable-next-line react/prop-types
const ActionChip = ({ children, color }) => {
	return (
		<Button
			round
			style={{
				backgroundColor: color,
				paddingTop: 5,
				paddingLeft: 9,
				paddingBottom: 5,
				paddingRight: 14,
				margin: 4,
			}}
		>
			{children}
		</Button>
	);
};

ActionChip.defaultProps = {
	color: "#EEEEEE",
};

const CourseDetails = observer(() => {
	const store = useContext(RootStoreContext);
	const courseStore = store.courseStore;

	const courseId = window.location.href.split("?id=").at(-1);
	const [course, setCourse] = useState();
	const [backgroundImageURI, setBackgroundImageURI] = useState(backgroundImage);

	useEffect(() => {
		courseStore.fetchCourse(courseId);
	}, []);

	useEffect(() => {
		if (!courseStore.isLoading) {
			const course = courseStore.getCourseById(courseId);
			setCourse(course);
			if (course.image) {
				setBackgroundImageURI(course.image);
			} else {
				const categoryImageURI = getCategoryImageURI(course.category);
				setBackgroundImageURI(categoryImageURI);
			}
		}
	}, [courseStore.isLoading]);

	const classes = style();
	const imageClasses = classNames(
		classes.imgRaised,
		classes.imgRoundedCircle,
		classes.imgFluid,
	);

	return (
		<div>
			{course?.title && course?.clubName && (
				<Helmet>
					<title>
						{course.title} - {course.club.name} - Solingen Sportapp
					</title>
				</Helmet>
			)}
			<SportappHeader />
			<Parallax
				image={backgroundImageURI}
				filter="dark"
				className={classes.parallax}
			/>
			{course && (
				<div className={classNames(classes.main, classes.mainRaised)}>
					<div className={classes.container}>
						<GridContainer justifyContent="center">
							<GridItem xs={12} sm={12} md={12}>
								<div className={classes.profile}>
									<div className={classes.imgWrapper}>
										<ClubLogoImg
											clubLogo={course.club.logo}
											alt={course.title}
											imgClasses={imageClasses}
										/>
									</div>
									<div className={classes.name}>
										<h3 className={classes.title} style={{ marginBottom: 0 }}>
											{course.title}
										</h3>
										<h5>{course.club.name}</h5>
									</div>
									<div
										dangerouslySetInnerHTML={{
											__html: course.descriptionAsHtml,
										}}
									/>
									<p className={classes.description}>
										{course.targetGroup
											? displayTargetGroup(course.targetGroup)
											: ""}
									</p>
									<GridContainer>
										{course.schedule && (
											<GridItem sm={12} md={6}>
												<GridContainer>
													<GridItem sm={1} md={1} />
													<GridItem sm={11} md={11}>
														<GridContainer style={{ textAlign: "left" }}>
															<GridItem xs={1}>
																<TodayIcon className={classes.subHeaderIcon} />
															</GridItem>
															<GridItem
																xs={10}
																className={classes.subSectionContent}
															>
																<h4
																	className={classNames(
																		classes.left,
																		classes.title,
																	)}
																>
																	Termine
																</h4>
																<p>
																	Vom{" "}
																	{new Date(
																		course.schedule.startingDate,
																	).toLocaleDateString()}{" "}
																	bis zum{" "}
																	{new Date(
																		course.schedule.finalDate,
																	).toLocaleDateString()}
																	:
																</p>
																{course.schedule.schedules.map(
																	(schedule, index) => {
																		return (
																			<div
																				key={index}
																				className={classes.listItem}
																			>
																				<p className={classes.listItemLeft}>
																					{mapDayEnumsToFullName(schedule.day)}:
																				</p>
																				<p>
																					{schedule.from} - {schedule.to}
																				</p>
																			</div>
																		);
																	},
																)}
															</GridItem>
														</GridContainer>
													</GridItem>
												</GridContainer>
											</GridItem>
										)}
										{course.costs && (
											<GridItem sm={12} md={6}>
												<GridContainer>
													<GridItem sm={1} md={1} />
													<GridItem sm={11} md={11}>
														<GridContainer style={{ textAlign: "left" }}>
															<GridItem xs={1}>
																<EuroSymbolIcon
																	className={classes.subHeaderIcon}
																/>
															</GridItem>
															<GridItem
																xs={10}
																className={classes.subSectionContent}
															>
																<h4
																	className={classNames(
																		classes.left,
																		classes.title,
																	)}
																>
																	Kosten
																</h4>
																{course.costs.map((singleCost, index) => {
																	if (
																		!("ageGroup" in singleCost) &&
																		!("cost" in singleCost) &&
																		!("billingInterval" in singleCost)
																	)
																		return null;
																	const { ageGroup, cost, billingInterval } =
																		singleCost;
																	const costWithoutEuroSign = cost
																		? cost.replace("€", "")
																		: "0";
																	return (
																		<div
																			key={index}
																			className={classes.listItem}
																		>
																			{ageGroup && (
																				<p className={classes.listItemLeft}>
																					{mapAgeGroupEnums(ageGroup)}:
																				</p>
																			)}
																			{costWithoutEuroSign.match(
																				/^[., 0-9]+$/,
																			) ? (
																				<p>
																					{costPerBillingIntervalMessage(
																						costWithoutEuroSign,
																						billingInterval,
																					)}
																				</p>
																			) : (
																				<p>{cost}</p>
																			)}
																		</div>
																	);
																})}
																<p>{course.hints}</p>
															</GridItem>
														</GridContainer>
													</GridItem>
												</GridContainer>
											</GridItem>
										)}
										{course.contact && (
											<GridItem sm={12} md={6}>
												<GridContainer>
													<GridItem sm={1} md={1} />
													<GridItem sm={11} md={11}>
														<GridContainer style={{ textAlign: "left" }}>
															<GridItem xs={1}>
																<GroupIcon className={classes.subHeaderIcon} />
															</GridItem>
															<GridItem
																xs={10}
																className={classes.subSectionContent}
															>
																<h4
																	className={classNames(
																		classes.left,
																		classes.title,
																	)}
																>
																	{getSafeFullName(course.contact)}
																</h4>
																{course.contact.email &&
																	course.contact.email != "" && (
																		<p>E-Mail: {course.contact.email}</p>
																	)}
																{course.contact.phone &&
																	course.contact.phone != "" && (
																		<p>Telefon: {course.contact.phone}</p>
																	)}
																<div className={classes.actionChipsWrapper}>
																	<MailTo
																		email={course.contact.email}
																		subject={course.title}
																		body={""}
																	>
																		<ActionChip>
																			<div
																				className={classes.actionChipContent}
																			>
																				<MailOutlineIcon />
																				<p>Anschreiben</p>
																			</div>
																		</ActionChip>
																	</MailTo>
																	{course.contact.phone &&
																		course.contact.phone != "" && (
																			<PhoneCall number={course.contact.phone}>
																				<ActionChip>
																					<div
																						className={
																							classes.actionChipContent
																						}
																					>
																						<PhoneIcon />
																						<p>Anrufen</p>
																					</div>
																				</ActionChip>
																			</PhoneCall>
																		)}
																</div>
															</GridItem>
														</GridContainer>
													</GridItem>
												</GridContainer>
											</GridItem>
										)}
										{course.location &&
											course.location.street &&
											course.location.houseNumber &&
											course.location.zipCode &&
											course.location.city && (
												<GridItem sm={12} md={6}>
													<GridContainer>
														<GridItem sm={1} md={1} />
														<GridItem sm={11} md={11}>
															<GridContainer style={{ textAlign: "left" }}>
																<GridItem xs={1}>
																	<LocationOn
																		className={classes.subHeaderIcon}
																	/>
																</GridItem>
																<GridItem
																	xs={10}
																	className={classes.subSectionContent}
																>
																	<h4
																		className={classNames(
																			classes.left,
																			classes.title,
																		)}
																	>
																		Adresse: {course.location.name}
																	</h4>
																	<p>
																		{course.location.street}{" "}
																		{course.location.houseNumber}
																	</p>
																	<p>
																		{course.location.zipCode}{" "}
																		{course.location.city}
																	</p>
																</GridItem>
															</GridContainer>
														</GridItem>
													</GridContainer>
												</GridItem>
											)}
									</GridContainer>
								</div>
							</GridItem>
						</GridContainer>
						<div style={{ height: 20 }} />
					</div>
				</div>
			)}
			<Footer
				content={
					<div>
						<div className={classes.left}>
							<List className={classes.list}>
								<ListItem className={classes.inlineBlock}>
									<a href="/impressum/" className={classes.block}>
										Impressum
									</a>
								</ListItem>
								<ListItem className={classes.inlineBlock}>
									<a href="/datenschutz/" className={classes.block}>
										Datenschutzerklärung
									</a>
								</ListItem>
							</List>
						</div>
						<div className={classes.right}>
							&copy; {1900 + new Date().getYear()} Solinger Sportbund e.V.
						</div>
					</div>
				}
			/>
		</div>
	);
});

export default CourseDetails;
